class ImageService {
  public validateImageFile(file: File): boolean {
    if (file.size > 7 * 1024 * 1024) { // 7MB limit
      alert('Image size exceeds 7MB limit.');
      return false;
    }

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      alert('Only PNG and JPEG images are supported.');
      return false;
    }

    return true;
  }

  public async imageToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const b64 = reader.result.split(',')[1];
          resolve(b64);
        } else {
          reject(new Error('Failed to read image data'));
        }
      };
      reader.onerror = (e) => reject(new Error('Failed to read image data: ' + e));
      reader.readAsDataURL(blob);
    });
  }

  // Read image file as base64
  public async readFileAsBase64(file: File): Promise<string> {

    const b64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (!reader.result || typeof reader.result !== 'string') {
          reject('Failed to read image file');
          return;
        }

        const b64 = reader.result.split(',')[1];
        resolve(b64);
      };
      reader.onerror = () => reject('Failed to read image file');
      reader.readAsDataURL(file);
    });
    return b64;
  }

  public async getFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
    const files: File[] = [];

    let imageProcessed = false;
    try {
      // Method 1: Check clipboard items (works on most desktop browsers)
      const items = Array.from(dataTransfer.items);
      const imageItem = items.find(item => item.type.startsWith('image/'));

      if (imageItem) {
        const file = imageItem.getAsFile();
        if (file && imageService.validateImageFile(file)) {
          files.push(file);
          imageProcessed = true;
        }
      }

      // Method 2: Check clipboard files (Android fallback)
      if (!imageProcessed && dataTransfer.files && dataTransfer.files.length > 0) {
        const files = Array.from(dataTransfer.files);
        const imageFile = files.find(file => file.type.startsWith('image/'));
        
        if (imageFile && imageService.validateImageFile(imageFile)) {
          files.push(imageFile);
          imageProcessed = true;
        }
      }

      // Method 3: Modern Clipboard API fallback (for newer browsers)
      if (!imageProcessed && navigator.clipboard && navigator.clipboard.read) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const clipboardItem of clipboardItems) {
            for (const type of clipboardItem.types) {
              if (type.startsWith('image/')) {
                const blob = await clipboardItem.getType(type);
                // Convert blob to file
                const file = new File([blob], 'pasted-image.' + type.split('/')[1], { type });
                if (imageService.validateImageFile(file)) {
                  files.push(file);
                  imageProcessed = true;
                  break;
                }
              }
            }
            if (imageProcessed) break;
          }
        } catch (clipboardError) {
          // Clipboard API not supported or permission denied, continue with other methods
          console.log('Clipboard API not available:', clipboardError);
        }
      }

    } catch (error) {
      console.error('Error processing pasted image:', error);
      alert('Failed to process the pasted image.');
    }

    return files;
  }

  public detectImageTypeFast(base64: string): 'image/png' | 'image/jpeg' | 'image/unknown' {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const prefix = base64Data.substring(0, 12);
    const binary = atob(prefix);
    
    // PNG signature: 89 50 4E 47 (iVBORw in base64)
    if (binary.charCodeAt(0) === 0x89 && 
        binary.charCodeAt(1) === 0x50 && 
        binary.charCodeAt(2) === 0x4E && 
        binary.charCodeAt(3) === 0x47) {
      return 'image/png';
    }
    
    // JPEG signature: FF D8 FF
    if (binary.charCodeAt(0) === 0xFF && 
        binary.charCodeAt(1) === 0xD8 && 
        binary.charCodeAt(2) === 0xFF) {
      return 'image/jpeg';
    }
    
    return 'image/unknown';
  }

  // Supported attachment MIME types
  private static SUPPORTED_ATTACHMENT_TYPES = [
    'image/png', 'image/jpeg',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    // Audio types
    'audio/mpeg', 'audio/mp3', 'audio/webm', 'audio/ogg', 'audio/wav',
    'audio/x-wav', 'audio/mp4', 'audio/x-m4a', 'audio/flac', 'audio/aac'
  ];

  private static SUPPORTED_EXTENSIONS = [
    '.png', '.jpg', '.jpeg',
    '.pdf',
    '.doc', '.docx',
    '.txt', '.md', '.csv',
    // Programming languages
    '.js', '.ts', '.jsx', '.tsx',
    '.py', '.java', '.cpp', '.c', '.h', '.hpp',
    '.cs', '.go', '.rb', '.php', '.swift', '.kt',
    '.rs', '.scala', '.r', '.m', '.mm',
    // Web files
    '.html', '.css', '.scss', '.sass', '.less',
    '.xml', '.svg',
    // Config/data files
    '.json', '.yaml', '.yml', '.toml', '.ini', '.cfg',
    '.log', '.sh', '.bat', '.ps1',
    // SQL and databases
    '.sql',
    // Other text files
    '.tex', '.rst', '.diff', '.patch',
    // Audio files
    '.mp3', '.ogg', '.wav', '.m4a', '.flac', '.aac'
  ];

  private static MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20MB for documents
  private static MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB for audio files

  public isAudioFile(file: File): boolean {
    return file.type.startsWith('audio/') ||
      ['.mp3', '.ogg', '.wav', '.m4a', '.flac', '.aac', '.webm'].some(
        ext => file.name.toLowerCase().endsWith(ext) && !file.type.startsWith('video/')
      );
  }

  public isFileSupported(file: File): boolean {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return file.type.startsWith('audio/') ||
      ImageService.SUPPORTED_ATTACHMENT_TYPES.includes(file.type) ||
      ImageService.SUPPORTED_EXTENSIONS.includes(ext);
  }

  public isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  public validateAttachmentFile(file: File): boolean {
    if (this.isAudioFile(file)) {
      if (file.size > ImageService.MAX_AUDIO_SIZE) {
        alert(`Audio file size exceeds 10MB limit.`);
        return false;
      }
    } else if (file.size > ImageService.MAX_ATTACHMENT_SIZE) {
      alert(`File size exceeds ${ImageService.MAX_ATTACHMENT_SIZE / 1024 / 1024}MB limit.`);
      return false;
    }

    if (!this.isFileSupported(file)) {
      alert('Unsupported file type. Supported: images, PDF, DOC, DOCX, audio (MP3, WebM, OGG, WAV, M4A), and most text-based files.');
      return false;
    }

    return true;
  }

  /**
   * Get files from data transfer, supporting both images and documents.
   * For large pasted text, creates a text-fragment File.
   */
  public async getAttachmentsFromDataTransfer(dataTransfer: DataTransfer, forceAttachment: boolean): Promise<{ files: File[], textFragment: string | null }> {
    const files: File[] = [];
    let textFragment: string | null = null;

    try {
      // Check for files first (images + documents)
      const items = Array.from(dataTransfer.items);

      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && this.isFileSupported(file) && this.validateAttachmentFile(file)) {
            files.push(file);
          }
        }
      }

      // Fallback to dataTransfer.files
      if (files.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
        for (const file of Array.from(dataTransfer.files)) {
          if (this.isFileSupported(file) && this.validateAttachmentFile(file)) {
            files.push(file);
          }
        }
      }

      // Check if large text is being pasted (text fragment)
      if (files.length === 0) {
        const textItem = items.find(item => item.kind === 'string' && item.type === 'text/plain');
        if (textItem) {
          const text = await new Promise<string>((resolve) => {
            textItem.getAsString(resolve);
          });
          // If pasted text is larger than 2000 chars or has more than 25 lines, treat as text fragment attachment
          const linesCount = text.split('\n').length;
          if (text.length > 2000 || linesCount > 25 || forceAttachment) {
            textFragment = text;
          }
        }
      }
    } catch (error) {
      console.error('Error processing pasted data:', error);
    }

    return { files, textFragment };
  }

  public createTextFragmentFile(text: string): File {
    const blob = new Blob([text], { type: 'text/plain' });
    const timestamp = Date.now();
    return new File([blob], `text-fragment-${timestamp}.txt`, { type: 'text/plain' });
  }
}
export const imageService = new ImageService();